/**
 * App-level toast helpers.
 *
 * Thin imperative wrappers over the Coss Toast manager (Base UI) so callers
 * fire transient operation results without pulling in the manager API or a
 * hook. i18n stays at the call site: pass an already-translated message.
 *
 * Mount <ToastProvider> once (see App.tsx) for these to render.
 */

import { toastManager } from "@/components/ui/toast";

// Base UI ToastProvider's default timeout is 5000ms, but we pass it explicitly
// here so the duration is visible at the call site and protected from any
// future default change.
const TOAST_TIMEOUT_MS = 5000;

/** Transient success (e.g. feedback submitted, audit deleted). */
export function notifySuccess(message: string): string {
  return toastManager.add({
    title: message,
    type: "success",
    timeout: TOAST_TIMEOUT_MS,
  });
}

/** Transient failure of an operation the user just triggered. */
export function notifyError(message: string): string {
  return toastManager.add({
    title: message,
    type: "error",
    priority: "high",
    timeout: TOAST_TIMEOUT_MS,
  });
}

/** Transient neutral notice (e.g. a user-canceled action, not an error). */
export function notifyInfo(message: string): string {
  return toastManager.add({
    title: message,
    type: "info",
    timeout: TOAST_TIMEOUT_MS,
  });
}
